import React, { useState, useEffect } from "react";
import { CommuteDataResponse } from "../api";
import { Alert } from "../api/alerts";
import { fetchAlerts, fetchCommuteData, StopConfig } from "./api-client";

import { LocationToggle } from "./LocationToggle";
import { AlertList } from "./Alerts";
import { BusETAList } from "./BusETA";
import { CabiStationList } from "./CabiStations";
import { TrainETAList } from "./TrainETA";
import { Setup } from "./Setup";

// Alerts change on the order of hours, so they poll far less often than the
// 10s ETA refresh and never block the dashboard's first paint.
const ALERT_REFRESH_MS = 2 * 60 * 1000;

const parseStopParam = (param: string | null): string[] => {
  if (!param) return [];
  return param.split(",").map((s) => s.trim()).filter(Boolean);
};

const readStopConfig = (search: string, prefix: "home" | "work"): StopConfig => {
  const params = new URLSearchParams(search);
  return {
    busStops: parseStopParam(params.get(`${prefix}Bus`)),
    metroStations: parseStopParam(params.get(`${prefix}Metro`)),
    cabiStations: parseStopParam(params.get(`${prefix}Cabi`)),
  };
};

const hasAnyStops = (config: StopConfig): boolean =>
  config.busStops.length > 0 ||
  config.metroStations.length > 0 ||
  config.cabiStations.length > 0;

const App: React.FC = () => {
  const isSetup = window.location.pathname === "/setup";

  const homeConfig = readStopConfig(window.location.search, "home");
  const workConfig = readStopConfig(window.location.search, "work");
  const hasConfig = hasAnyStops(homeConfig) || hasAnyStops(workConfig);

  // If no stops configured and not already on /setup, redirect
  if (!hasConfig && !isSetup) {
    window.location.href = "/setup";
    return null;
  }

  if (isSetup) {
    return <Setup homeConfig={homeConfig} workConfig={workConfig} />;
  }

  return <Dashboard homeConfig={homeConfig} workConfig={workConfig} />;
};

type DashboardProps = {
  homeConfig: StopConfig;
  workConfig: StopConfig;
};

const Dashboard: React.FC<DashboardProps> = ({ homeConfig, workConfig }) => {
  const [currentLocation, setCurrentLocation] = useState<"home" | "work">("home");
  const [commuteData, setCommuteData] = useState<CommuteDataResponse | undefined>();
  const [alerts, setAlerts] = useState<Alert[] | undefined>();

  const stops = currentLocation === "home" ? homeConfig : workConfig;

  const fetchAndUpdate = (location: "home" | "work", stopConfig: StopConfig) => {
    fetchCommuteData(location, stopConfig).then(setCommuteData);
  };

  useEffect(() => {
    setCommuteData(undefined);
    fetchAndUpdate(currentLocation, stops);
    const id = setInterval(() => fetchAndUpdate(currentLocation, stops), 10 * 1000);
    return () => clearInterval(id);
  }, [currentLocation]);

  // Alerts are systemwide, so this does not depend on the selected location —
  // only the filtering in AlertList does.
  useEffect(() => {
    const loadAlerts = () => fetchAlerts().then(setAlerts, () => setAlerts([]));
    loadAlerts();
    const id = setInterval(loadAlerts, ALERT_REFRESH_MS);
    return () => clearInterval(id);
  }, []);

  return (
    <>
      <LocationToggle
        currentLocation={currentLocation}
        onToggleCurrentLocation={(loc) => {
          setCommuteData(undefined);
          setCurrentLocation(loc as "home" | "work");
        }}
      />
      <AlertList
        alerts={alerts}
        busETAs={commuteData?.busETAs}
        trainETAs={commuteData?.trainETAs}
      />
      <BusETAList busETAs={commuteData?.busETAs} />
      <TrainETAList trainETAs={commuteData?.trainETAs} />
      <CabiStationList cabiStations={commuteData?.cabiStations} />
      <p className="setup-tip dashboard-setup-link">
        Change your stops and bike shares at <a href={`/setup${window.location.search ? `?${new URLSearchParams(window.location.search).toString()}` : ""}`}>setup</a>.
      </p>
    </>
  );
};

export default App;
